import { Form, Formik, FormikErrors } from 'formik';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createFreightOrder, getBusinessUnit } from '../../store/APISlice';
import { useAppDispatch } from '../../store/store';
import { getValidationSchema } from '../../validationSchemas';
import BillingInformation from '../BillingInformationComponent/BillingInformation';
import BillingInformationPTRA from '../BillingInformationPTRAComponent/BillingInformationPTRA';
import Footer from '../FooterComponent/Footer';
import Header from '../HeaderComponent/Header';
import ProductHandlingInformation from '../ProductHandlingComponent/ProductHandlingInformation';
import ReceivingInformation from '../ReceivingInformationComponent/ReceivingInformation';
import RequestersInformation from '../RequestersInformationComponent/RequestersInformation';
import ShippingInformation from '../ShippingInformationComponent/ShippingInformation';
import StepperComponent from '../StepperComponent/CustomStepper';
import UserProfile from '../userProfile/UserProfile';
import styles from "./MainComponent.module.css";

const initialValues = {
  alternateName: '',
  alternateEmail: '',
  alternatePhoneNumber: '',
  purchaseOrderNumberList: [],
  purchaseOrderNumber: '',
  pickupNumber: '',
  requesterName: '',
  requesterEmail: '',
  requesterPhoneNumber: '',
  contactRows: [{ contactName: '', email: '', phoneNumber: '' }],
  shippingCompanyName: '',
  shippingStreetAddress: '',
  shippingCity: '',
  shippingState: '',
  shippingZipCode: '',
  shippingLocationID: '',
  shippingDockDoor: '',
  shippingDockHeightFlag: '',
  shippingProductReadyDate: '',
  shippingProductReadyTime: '',
  shipperCloseTime: '',
  shippingAdditionalComments: '',
  shippingSchedulingType: '',
  shippingContactName: '',
  shippingContactEmail: '',
  shippingContactPhoneNumber: '',
  receivingCompanyName: '',
  receivingStreetAddress: '',
  receivingCity: '',
  receivingState: '',
  receivingZipCode: '',
  receivingLocationID: '',
  receivingDockDoor: '',
  receivingDockHeightFlag: '',
  receivingRequiredDate: '',
  receivingRequiredTime: '',
  receivingCloseTime: '',
  receivingAdditionalComments: '',
  receivingSchedulingType: '',
  receivingContactName: '',
  receivingContactPhoneNumber: '',
  receivingContactEmail: '',
  requestType: '',
  commodity: '',
  quantity: '',
  containerType: '',
  productDimension: ['', '', ''], // Initial values for length, height, and width
  totalWeightLbs: '',
  loadValue: '',
  reeferRequired: '',
  hazmatFlag: '',
  expediteServiceFlag: '',
  fullTruckLoadFlag: '',
  teamDeliveryFlag: '',
  specialHandlingRequired: '',
  freightClass: '',
  receivingContactRows: [{ contactName: '', email: '', phoneNumber: '' }],
  temperatureRequirement: ['', ''],
  hazmatUnNumber: '',
  hazmatClass: '',
  packagingGroup: '',
  handlingComments: '',
  specialHandlingTypeList: '',
  teamDeliveryApproverEmail: '',
  expediteApproverEmail: '',
  isLoadParcel: '',
  dedicatedTransportFlag: '',
  dedicatedApproverEmail: '',
  programInfo: '',
  researchAndDevelopmentFlag: '',
  arlingtonOrGrandPrairieLocationFlag: '',
  pepLoc: '',
  accountProjectNumber: '',
  costCenter: '',
  divLoc: '',
  shippingAlternateContacts: [{
    contactName: '',
    contactEmail: '',
    contactPhoneNumber: '',
  }],
  receivingAlternateContacts: [{
    contactName: '',
    contactEmail: '',
    contactPhoneNumber: '',
  }],
  requesterAlternateContacts: [{
    contactName: '',
    contactEmail: '',
    contactPhoneNumber: '',
  }],
  commodityOtherValue: '',
  additionalContactsList: [],
  additionalReceivingContactsList: [],
  companyCode: '',
  profitCenter: ''

};

interface AdditionalContact {
  alternateShippingContactName: string;
  alternateShippingContactEmail: string;
  alternateShippingContactPhoneNumber: string;
}

interface AdditionalReceivingContact {
  alternateReceivingContactName: string;
  alternateReceivingContactEmail: string;
  alternateReceivingContactPhoneNumber: string;
}

const MainComponent = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const steps = ['Billing Information', 'Requester Information', 'Shipping Information', 'Receiving Information', 'Product & Handling Information'];
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const selectedBu = useSelector(getBusinessUnit);
  const [contacts, setContacts] = useState<AdditionalContact[]>([]);
  const [receivingContacts, setReceivingContacts] = useState<AdditionalReceivingContact[]>([]);
  const description = `PTS Order Enter Request - ${selectedBu}`;

  const handleContactsChange = (newContacts: AdditionalContact[]) => {
    setContacts(newContacts);
  };

  const handleReceivingContactsChange = (newContacts: AdditionalReceivingContact[]) => {
    setReceivingContacts(newContacts);
  };

  const handleNext = (validateForm: () => Promise<FormikErrors<any>>, setFieldTouched: (arg0: string, arg1: boolean, arg2: boolean) => void, setErrors: (errors: FormikErrors<any>) => void, values: any) => {
    validateForm().then((errors: FormikErrors<any>) => {


      if (Object.keys(errors).length === 0) {
        // Update values with alternate contacts
        // Update values with alternate contacts for shipping
        if (values.additionalContactsList && values.additionalContactsList.length > 0) {
          values.shippingAlternateContacts = values.additionalContactsList.map((contact: any) => ({
            contactName: contact.alternateShippingContactName,
            contactEmail: contact.alternateShippingContactEmail,
            contactPhoneNumber: contact.alternateShippingContactPhoneNumber,
          }));
        } else {
          values.shippingAlternateContacts = [];
        }


        // Update values with alternate contacts for receiving
        if (values.additionalReceivingContactsList && values.additionalReceivingContactsList.length > 0) {
          values.receivingAlternateContacts = values.additionalReceivingContactsList.map((contact: any) => ({
            contactName: contact.alternateReceivingContactName,
            contactEmail: contact.alternateReceivingContactEmail,
            contactPhoneNumber: contact.alternateReceivingContactPhoneNumber,
          }));
        } else {
          values.receivingAlternateContacts = [];
        }


        // Delete additionalContactsList and additionalReceivingContactsList from values before submission
        //delete values.additionalContactsList;
        //delete values.additionalReceivingContactsList;

        if (currentStep === steps.length - 1) {
          handleSubmit(values);
        } else {
          setCurrentStep((prevStep) => prevStep + 1);
          setErrors({});
        }
      } else {
        Object.keys(errors).forEach((field) => {
          setFieldTouched(field, true, true);
        });

        // Explicitly set Length, Height, and Width as touched if they are in the errors
        if (errors.productDimension) {
          if (typeof errors.productDimension === 'object' && 'length' in errors.productDimension) {
            setFieldTouched('productDimension.length', true, true);
          }
          if (typeof errors.productDimension === 'object' && 'height' in errors.productDimension) {
            setFieldTouched('productDimension.height', true, true);
          }
          if (typeof errors.productDimension === 'object' && 'width' in errors.productDimension) {
            setFieldTouched('productDimension.width', true, true);
          }
          if (typeof errors.productDimension === 'object') {
            setFieldTouched('productDimension', true, true);
          }
        }

        // Explicitly set temperatureMin, temperatureMax, and temperatureRequired as touched if they are in the errors
        if (errors.temperatureRequirement) {
          if (typeof errors.temperatureRequirement === 'object' && 'temperatureMin' in errors.temperatureRequirement) {
            setFieldTouched('temperatureRequirement.temperatureMin', true, true);
          }
          if (typeof errors.temperatureRequirement === 'object' && 'temperatureMax' in errors.temperatureRequirement) {
            setFieldTouched('temperatureRequirement.temperatureMax', true, true);
          }
          if (typeof errors.temperatureRequirement === 'object' && 'temperatureRequired' in errors.temperatureRequirement) {
            setFieldTouched('temperatureRequirement.temperatureRequired', true, true);
          }
        }

        if (Array.isArray(errors.contacts)) {
          errors.contacts.forEach((contactError, index) => {

            if (typeof contactError === 'object' && contactError.alternateShippingContactName) {

              setFieldTouched(`contacts[${index}].alternateShippingContactName`, true, true);
            }
            if (typeof contactError === 'object' && contactError.alternateShippingContactEmail) {

              setFieldTouched(`contacts[${index}].alternateShippingContactEmail`, true, true);
            }
            if (typeof contactError === 'object' && contactError.alternateShippingContactPhoneNumber) {

              setFieldTouched(`contacts[${index}].alternateShippingContactPhoneNumber`, true, true);
            }
          });
        }


        // Explicitly set contactName, email, and phoneNumber as touched for all rows in contactRows if they are in the errors
        /*  if (errors.alternateShippingContactName) {
           setFieldTouched('alternateShippingContactName', true, true);
         }
 
         if (errors.alternateShippingContactEmail) {
           setFieldTouched('alternateShippingContactEmail', true, true);
         }
 
         if (errors.alternateShippingContactPhoneNumber) {
           setFieldTouched('alternateShippingContactPhoneNumber', true, true);
         } */

        if (errors.alternateReceivingContactName) {
          setFieldTouched('alternateReceivingContactName', true, true);
        }

        if (errors.alternateReceivingContactEmail) {
          setFieldTouched('alternateReceivingContactEmail', true, true);
        }

        if (errors.alternateReceivingContactPhoneNumber) {
          setFieldTouched('alternateReceivingContactPhoneNumber', true, true);
        }
      }
    });
  };

  const onBack = (setErrors: (errors: FormikErrors<any>) => void, values: any) => {

    if (currentStep > 0) {
      setCurrentStep((prevStep) => prevStep - 1);
      setErrors({});
    } else {
      navigate('/Inbound-Automation'); // Adjust the navigation path as needed
    }
  };



  const renderStep = (currentStep: number, validateForm: any, setFieldTouched: any, setErrors: any, values: any, errors: any, touched: any) => {
    //deepLogValues(values);
    switch (currentStep) {
      case 0:
        return selectedBu === 'PTRA' ? (
          <BillingInformationPTRA
            onNext={() => handleNext(validateForm, setFieldTouched, setErrors, values)}
            onBack={() => onBack(setErrors, values)}
            values={values}
            errors={errors}
            touched={touched}
            requestType={selectedBu} // Add the appropriate requestType value here
          />
        ) : (
          <BillingInformation
            onNext={() => handleNext(validateForm, setFieldTouched, setErrors, values)}
            onBack={() => onBack(setErrors, values)}
            values={values}
            errors={errors}
            touched={touched}
            requestType={selectedBu} // Add the appropriate requestType value here
          />
        );
      case 1:
        return (
          <RequestersInformation
            onNext={() => handleNext(validateForm, setFieldTouched, setErrors, values)}
            onBack={() => onBack(setErrors, values)}
            values={values}
            errors={errors}
            touched={touched}
            alternateContactType="requester"

          />
        );
      case 2:
        return (
          <ShippingInformation
            onNext={() => handleNext(validateForm, setFieldTouched, setErrors, values)}
            onBack={() => onBack(setErrors, values)}
            values={values}
            errors={errors}
            touched={touched}
            contactRows={values.contactRows}
            onContactsChange={handleContactsChange}
          />
        );
      case 3:
        return (
          <ReceivingInformation
            onNext={() => handleNext(validateForm, setFieldTouched, setErrors, values)}
            onBack={() => onBack(setErrors, values)}
            values={values}
            errors={errors}
            touched={touched}
            receivingContactRows={values.receivingContactRows}
            onContactsChange={handleReceivingContactsChange}
          />
        );
      case 4:
        return (
          <ProductHandlingInformation
            onNext={() => handleNext(validateForm, setFieldTouched, setErrors, values)}
            onBack={() => onBack(setErrors, values)}
            values={values}
            errors={errors}
            touched={touched}
            setFieldValue={(field, value, shouldValidate) => {
              setFieldTouched(field, true, shouldValidate);
              values[field] = value;
            }}
            currentStep={currentStep}
            steps={steps}
          />
        );
      default:
        return null;
    }
  };

  const handleSubmit = async (values: any) => {
    values.requestType = selectedBu;
    values.shippingDockHeightFlag = values.shippingDockHeightFlag === 'Yes' ? true : false;
    values.receivingDockHeightFlag = values.receivingDockHeightFlag === 'Yes' ? true : false;
    //values.productDimension = `${values.productDimension.length}*${values.productDimension.height}*${values.productDimension.width}`;
    values.temperatureRequirement = values.reeferRequired === 'yes' ? `${values.temperatureRequirement[0]}-${values.temperatureRequirement[1]}` : '';
    values.reeferRequired = values.reeferRequired === 'yes';
    values.hazmatFlag = values.hazmatFlag === 'yes';
    values.expediteServiceFlag = values.expediteServiceFlag === 'yes';
    values.fullTruckLoadFlag = values.fullTruckLoadFlag === 'yes';
    values.teamDeliveryFlag = values.teamDeliveryFlag === 'yes';
    values.specialHandlingRequired = values.specialHandlingRequired === 'yes';
    values.commodityOtherValue = values.commodity === 'Others' ? values.commodityOtherValue : '';
    values.isLoadParcel = values.isLoadParcel !== "" && values.isLoadParcel !== null ? values.isLoadParcel === 'yes' : null;
    values.dedicatedTransportFlag = values.dedicatedTransportFlag === 'yes';
    if (!values?.programInfo || values.programInfo !== 'Pepsi') {
      values.researchAndDevelopmentFlag = values.researchAndDevelopmentFlag !== "" && values.researchAndDevelopmentFlag !== null ? values.researchAndDevelopmentFlag === 'Yes' : null;
    }
    values.arlingtonOrGrandPrairieLocationFlag = values.arlingtonOrGrandPrairieLocationFlag !== "" && values.arlingtonOrGrandPrairieLocationFlag !== null ? values.arlingtonOrGrandPrairieLocationFlag === 'Yes' : null;
    values.productDimension = `${values.productDimension[0]}*${values.productDimension[1]}*${values.productDimension[2]}`
    //values.dedicatedTransportFlag = values.isLoadParcel 
    if (values.alternateName && (values.alternateEmail || values.alternatePhoneNumber)) {
      const requesterAlternateContacts = [
        {
          contactName: values.alternateName,
          contactEmail: values.alternateEmail,
          contactPhoneNumber: values.alternatePhoneNumber,
        },
      ];
      values.requesterAlternateContacts = requesterAlternateContacts;
    }
    if (values.fullTruckLoadFlag && values.isLoadParcel === null) {
      values.dedicatedTransportFlag = '';
    } else if (!values.fullTruckLoadFlag && values.isLoadParcel === true) {
      values.dedicatedTransportFlag = '';
    } else if (!values.fullTruckLoadFlag && values.isLoadParcel === false) {
      values.dedicatedTransportFlag = values.dedicatedTransportFlag === 'yes';
    }

/* delete values.additionalContactsList;
delete values.additionalReceivingContactsList */;
    const finalValues = { ...values, contacts, receivingContacts };

    //deepLogValues(finalValues);
    try {
      await dispatch(createFreightOrder(finalValues));
      navigate('/success'); // Navigate on successful submission
    } catch (error) {
      console.error('Error:', error);
      navigate('/error', { state: { error } }); // Navigate on error
    }
  };

  return (
    <>
      <UserProfile />
      <Formik
        initialValues={initialValues}
        validationSchema={getValidationSchema(currentStep, selectedBu)}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ validateForm, errors, touched, values, setFieldTouched, setErrors, handleChange }) => (
          <Form>
            <div style={{ flex: '0 1 auto' }}>
              <Header headDescription={description} />
            </div>
            <div className={styles.mainDiv}>
              <div className={styles.childDiv}>
                <StepperComponent currentStep={currentStep} steps={steps} />
                <div className="scrollable-content">
                  {renderStep(currentStep, validateForm, setFieldTouched, setErrors, values, errors, touched)}
                </div>
              </div>
            </div>
            <div style={{ flex: '0 1 auto', position: 'fixed', bottom: 0, width: '100%', backgroundColor: 'white', overflow: 'hidden', zIndex: '2' }}>
              <Footer />
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default MainComponent;